import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Remembers scroll position per URL. On back/forward (POP) it restores the exact
// position you were last viewing; on new navigations it scrolls to top.
// Positions are keyed by pathname+search (stable across setSearchParams churn).
// During a navigation transition the outgoing page briefly reports a clamped scroll
// (the incoming shorter page clamps the window), which would overwrite the real
// saved value. We block saves for the whole transition (set synchronously in a
// layout effect, before the queued transient scroll event fires) and, on POP,
// re-apply the target every frame until reached to handle async content growth.
const positions = new Map();
let restoring = false;

const ScrollToTop = () => {
  const location = useLocation();
  const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"
  const key = location.pathname + location.search;
  const keyRef = useRef(key);
  keyRef.current = key;

  // Mount once: manual browser restoration + one continuous position recorder.
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const save = () => {
      if (restoring) return;
      positions.set(keyRef.current, window.scrollY);
    };
    window.addEventListener("scroll", save, { passive: true });
    return () => window.removeEventListener("scroll", save);
  }, []);

  // Runs synchronously on navigation, before the transient scroll event handler,
  // so the clamped value can't overwrite the saved position.
  useLayoutEffect(() => {
    restoring = true;
  }, [key]);

  useEffect(() => {
    const target = navType === "POP" ? (positions.get(key) ?? 0) : 0;

    if (target <= 0) {
      window.scrollTo(0, 0);
      const t = setTimeout(() => { restoring = false; }, 350);
      return () => clearTimeout(t);
    }

    const start = performance.now();
    let raf = 0;
    const loop = () => {
      window.scrollTo(0, target);
      const reached = Math.abs(window.scrollY - target) <= 2;
      if (reached || performance.now() - start > 3000) {
        restoring = false;
        return;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => { restoring = false; cancelAnimationFrame(raf); };
  }, [key, navType]);

  return null;
};

export default ScrollToTop;
